import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useHistory } from "react-router-dom";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from "reactflow";
import { v4 as uuidv4 } from "uuid";
import RunAndTest from "components/play-ground/Run";
import BestMovesModels from "components/play-ground/BestMovesModels";
import toast from "react-hot-toast";
import CustomEdge from "./components/CustomEdge";
import SwitchNode from "./components/SwitchNode";
import GateNode from "./components/GateNode";
import OutputNode from "./components/OutputNode";
import GateSelectionModal from "./components/GateSelectionModel";
import { useCreateClassicLevelMutation } from "lib/store/service/classic";
import { Button } from "@mui/material";
import {
  PowerSettingsNew,
  Extension,
  EmojiEvents,
  PlayArrow,
  Publish,
  ArrowBack,
} from "@mui/icons-material";

function CreateClassicLevel() {
  const history = useHistory();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showGateModal, setShowGateModal] = useState(false);
  const [openRunModel, setOpenRunModel] = useState(false);
  const [openBestMovesModel, setOpenBestMovesModel] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const [createClassicLevel, { isLoading, data: addResData }] = useCreateClassicLevelMutation();
  const [finalData, setFinalData] = useState({});

  // Reset verification when canvas changes
  const handleNodesChange = (changes) => {
    setIsVerified(false);
    onNodesChange(changes);
  };

  const handleEdgesChange = (changes) => {
    setIsVerified(false);
    onEdgesChange(changes);
  };

  // ✅ Define Custom Edge Type
  const edgeTypes = useMemo(
    () => ({
      custom: (props) => <CustomEdge {...props} deleteEdge={deleteEdge} />,
    }),
    []
  );

  // ✅ Delete Edge by ID
  const deleteEdge = (id) => {
    setIsVerified(false);
    setEdges((prevEdges) => prevEdges.filter((edge) => edge.id !== id));
  };

  // ✅ Delete Node by ID
  const deleteNode = useCallback((id) => {
    setIsVerified(false);
    setNodes((prevNodes) => prevNodes.filter((node) => node.id !== id));
    setEdges((prevEdges) => prevEdges.filter((edge) => edge.source !== id && edge.target !== id));
  }, [setNodes, setEdges]);

  const toggleSwitchDefaultState = useCallback((id) => {
    setIsVerified(false);
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (node.id === id) {
          const current = node.data?.defaultState !== undefined ? node.data.defaultState : !!node.data?.isOn;
          const nextState = !current;
          toast.success(`Switch initial state set to ${nextState ? "ON 🟢" : "OFF 🔴"}`);
          return {
            ...node,
            data: {
              ...node.data,
              defaultState: nextState,
              isOn: nextState,
            },
          };
        }
        return node;
      })
    );
  }, [setNodes]);

  const nodeTypes = useMemo(
    () => ({
      input: (props) => (
        <SwitchNode
          {...props}
          deleteNode={deleteNode}
          data={{ ...props.data, onToggleDefaultState: toggleSwitchDefaultState }}
        />
      ),
      gate: (props) => <GateNode {...props} deleteNode={deleteNode} />,
      output: (props) => <OutputNode {...props} deleteNode={deleteNode} />,
    }),
    [toggleSwitchDefaultState, deleteNode]
  );

  // ✅ Handle Edge Connection
  const onConnect = useCallback(
    (params) => {
      setIsVerified(false);
      setEdges((prevEdges) => [...prevEdges, { ...params, id: uuidv4(), type: "custom" }]);
    },
    [setEdges]
  );

  const addGate = () => setShowGateModal(true);

  const addNode = useCallback((type) => {
    setIsVerified(false);
    setNodes((prevNodes) => {
      // Prevent adding more than one output node
      if (type === "output" && prevNodes.some((node) => node.type === "output")) {
        toast.error("Only one Level Complete target allowed!");
        return prevNodes;
      }

      const inputs = type === "gate" ? ["1", "2"] : type === "output" ? [""] : [];
      const newNode = {
        id: uuidv4(),
        type,
        position: {
          x: Math.random() * 300 + 150,
          y: Math.random() * 250 + 100,
        },
        draggable: true,
        data: {
          label: type === "input" ? "Switch" : type === "output" ? "Completion Light" : "Gate",
          inputs: inputs || [""],
          outputs: type === "output" ? [] : [""],
        },
      };

      return [...prevNodes, newNode];
    });
  }, []);

  const handleGateSelect = (type) => {
    setIsVerified(false);
    setNodes((prevNodes) => {
      const newNode = {
        id: uuidv4(),
        type: "gate",
        position: { x: Math.random() * 300 + 150, y: Math.random() * 250 + 100 },
        draggable: true,
        data: {
          label: `${type}`,
          inputs: type === "NOT" ? ["1"] : ["1", "2"],
          outputs: ["1"],
        },
      };
      return [...prevNodes, newNode];
    });
    setShowGateModal(false);
  };

  const saveLevel = () => {
    const levelData = {
      nodes: nodes.map((node) => {
        const isSwOn = node.data?.defaultState !== undefined ? !!node.data.defaultState : !!node.data?.isOn;
        return {
          id: node.id,
          type: node.type,
          data: {
            label: node.data?.label || (node.type === "input" ? "Switch" : node.type === "output" ? "Completion Light" : "OR"),
            inputs: node.type === "gate" ? (node.data?.label === "NOT" ? ["1"] : ["1", "2"]) : [],
            outputs: node.type === "gate" ? ["1"] : [],
            defaultState: node.type === "input" ? isSwOn : false,
            isOn: node.type === "input" ? isSwOn : false,
          },
          position: node.position,
        };
      }),
      edges: edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        sourceHandle: edge.sourceHandle || "output",
        target: edge.target,
        targetHandle: edge.targetHandle || "input-1",
        animated: true,
      })),
    };
    const outputId = levelData.nodes.find((obj) => obj.type === "output")?.id;
    if (!outputId) {
      return null;
    }
    return { levelData, outputId };
  };

  const handleRun = async () => {
    let data = await saveLevel();
    if (data) {
      setFinalData(data);
      setOpenRunModel(true);
    } else {
      toast.error("Please add logic nodes and a Level Complete target first!");
    }
  };

  const handleFinalPublish = async (data, bestMove, rewardCoins, difficulty) => {
    const { levelData, outputId } = data;
    const { nodes: nList, edges: eList } = levelData;

    toast.loading("🚀 Publishing level to campaign...", { id: "publish-loader" });
    try {
      await createClassicLevel({
        nodes: nList,
        edges: eList,
        completeNodeId: outputId,
        bestMoves: Number(bestMove),
        rewardCoins: Number(rewardCoins) || 10,
        difficulty: difficulty || "Medium",
      }).unwrap();
      toast.success("✅ Classic Level published successfully!", { id: "publish-loader" });
      setOpenBestMovesModel(false);
      history.push("/classic");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to publish level", { id: "publish-loader" });
    }
  };

  const openBestMovesModelFN = () => {
    if (!isVerified) {
      toast.error("⚠️ You must click 'Test Circuit' and successfully solve the level before publishing!", {
        duration: 4000,
        style: {
          background: "#0f172a",
          color: "#f59e0b",
          border: "1px solid #f59e0b",
          fontWeight: "bold",
          fontSize: "13px",
          borderRadius: "12px",
        },
      });
      return;
    }
    setOpenBestMovesModel(true);
  };

  const publishLevel = async (config) => {
    const bestMove = typeof config === "object" ? config.bestMoves : config;
    const rewardCoins = typeof config === "object" ? config.rewardCoins : 10;
    const difficulty = typeof config === "object" ? config.difficulty : "Medium";

    const data = await saveLevel();

    if (data) {
      const { levelData, outputId } = data;
      const { nodes: nList, edges: eList } = levelData;
      if (nList.length === 0) return toast.error("No nodes found");
      if (eList.length === 0) return toast.error("No edges found");
      if (!outputId) return toast.error("No Level Complete target found");

      handleFinalPublish(data, bestMove, rewardCoins, difficulty);
    } else {
      toast.error("Level structure incomplete! Ensure nodes and output target are connected.");
    }
  };

  useEffect(() => {
    if (addResData) {
      if (addResData.statusCode === 200 && addResData.response?.statusEnum === 1) {
        toast.success("Level created successfully!");
        history.push("/classic");
      }
    }
  }, [addResData, history]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card sx={{ p: 3, backgroundColor: "#0f172a", border: "1px solid #1e293b", borderRadius: "20px" }}>
          {/* Top Floating Glassmorphic Toolbar */}
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => history.push("/classic")}
                sx={{
                  color: "#94a3b8",
                  borderColor: "#334155",
                  fontSize: "12px",
                  borderRadius: "10px",
                  "&:hover": { borderColor: "#06b6d4", color: "#38bdf8" },
                }}
              >
                Back
              </Button>
              <VuiTypography variant="h5" color="white" fontWeight="bold">
                Classic Level Creator Studio
              </VuiTypography>
              {isVerified ? (
                <Chip
                  label="✓ CIRCUIT SOLVED & VERIFIED"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(16, 185, 129, 0.2)",
                    color: "#10b981 !important",
                    fontWeight: "bold",
                    fontSize: "10px",
                    border: "1px solid #10b981",
                  }}
                />
              ) : (
                <Chip
                  label="⚠️ UNTESTED - SOLVE TO PUBLISH"
                  size="small"
                  sx={{
                    backgroundColor: "rgba(245, 158, 11, 0.15)",
                    color: "#f59e0b !important",
                    fontWeight: "bold",
                    fontSize: "10px",
                    border: "1px solid #f59e0b",
                  }}
                />
              )}
            </VuiBox>

            {/* Circuit Node Building Tools */}
            <VuiBox display="flex" gap={1.5} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<PowerSettingsNew />}
                onClick={() => addNode("input")}
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  backgroundColor: "#10b981 !important",
                  color: "#ffffff !important",
                  borderRadius: "10px",
                  boxShadow: "0 0 10px rgba(16, 185, 129, 0.4)",
                  "&:hover": { backgroundColor: "#059669 !important" },
                }}
              >
                + Power Switch
              </Button>

              <Button
                variant="contained"
                startIcon={<Extension />}
                onClick={addGate}
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  backgroundColor: "#06b6d4 !important",
                  color: "#ffffff !important",
                  borderRadius: "10px",
                  boxShadow: "0 0 10px rgba(6, 182, 212, 0.4)",
                  "&:hover": { backgroundColor: "#0891b2 !important" },
                }}
              >
                + Logic Gate
              </Button>

              <Button
                variant="contained"
                startIcon={<EmojiEvents />}
                onClick={() => addNode("output")}
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  backgroundColor: "#38bdf8 !important",
                  color: "#ffffff !important",
                  borderRadius: "10px",
                  boxShadow: "0 0 10px rgba(56, 189, 248, 0.4)",
                  "&:hover": { backgroundColor: "#0284c7 !important" },
                }}
              >
                + Level Target
              </Button>

              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleRun}
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  backgroundColor: "#f59e0b !important",
                  color: "#ffffff !important",
                  borderRadius: "10px",
                  boxShadow: "0 0 10px rgba(245, 158, 11, 0.4)",
                  "&:hover": { backgroundColor: "#d97706 !important" },
                }}
              >
                Test Circuit
              </Button>

              <Button
                variant="contained"
                startIcon={<Publish />}
                onClick={openBestMovesModelFN}
                disabled={!isVerified}
                sx={{
                  fontSize: "11px",
                  fontWeight: "bold",
                  backgroundColor: isVerified ? "#06b6d4 !important" : "#334155 !important",
                  color: isVerified ? "#ffffff !important" : "#94a3b8 !important",
                  borderRadius: "10px",
                  px: 3,
                  boxShadow: isVerified ? "0 0 14px rgba(6, 182, 212, 0.5)" : "none",
                  opacity: isVerified ? 1 : 0.6,
                  cursor: isVerified ? "pointer" : "not-allowed",
                  "&:hover": { backgroundColor: isVerified ? "#0891b2 !important" : "#334155 !important" },
                }}
              >
                Publish Level 🚀
              </Button>
            </VuiBox>
          </VuiBox>

          {/* ReactFlow Interactive Canvas Container */}
          <VuiBox sx={{ width: "100%", height: "76vh", backgroundColor: "#090d16", borderRadius: "20px", border: "1px solid #1e293b", overflow: "hidden", position: "relative" }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onConnect={onConnect}
              fitView
              style={{ width: "100%", height: "100%" }}
            >
              <Background color="#334155" gap={24} size={1} />
              <Controls style={{ backgroundColor: "#1e293b", borderColor: "#334155", color: "#fff" }} />
            </ReactFlow>
          </VuiBox>
        </Card>
      </VuiBox>

      {showGateModal && (
        <GateSelectionModal onClose={() => setShowGateModal(false)} onSelect={handleGateSelect} />
      )}
      {openRunModel && (
        <RunAndTest
          onClose={() => setOpenRunModel(false)}
          data={finalData}
          onVerified={() => setIsVerified(true)}
        />
      )}
      {openBestMovesModel && (
        <BestMovesModels
          onClose={() => setOpenBestMovesModel(false)}
          setMoves={(config) => publishLevel(config)}
          isLoading={isLoading}
        />
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default CreateClassicLevel;
